<script setup>
import { computed } from "vue";

import { almConfig, ALM_SYNC_STATUS } from "@/almConfig";
import StatusDot from "@/components-ng/StatusDot.vue";
import SvgIcon from "@/components-ng/SvgIcon.vue";
import UserImage from "@/components/UserImage.vue";
import { toMediumDateString } from "@/utils/dateFormat";

const props = defineProps({
  creationDate: { type: Number, default: null },
  // Null while it loads, and for a session with no resolvable `created_by`
  creator: { type: Object, default: null },
  // { almType, connectionId, status, name } — null when the session has no
  // ALM connection at all
  alm: { type: Object, default: null },
  // The events page is admin-only, so a non-admin gets the same card unlinked
  // rather than a link the router turns them away from
  canOpenEvents: { type: Boolean, default: false },
  // Before the list's own request lands, "none" and "not asked" look alike
  almPending: { type: Boolean, default: false },
});

const provider = computed(() => almConfig(props.alm?.almType));
const status = computed(() => ALM_SYNC_STATUS[props.alm?.status]);

const created = computed(() => {
  return props.creationDate ? toMediumDateString(new Date(props.creationDate * 1000)) : "—";
});

const eventsLink = computed(() =>
  props.canOpenEvents && props.alm?.connectionId
    ? {
        name: "almConnectionEvents",
        params: { almType: props.alm.almType, connectionId: props.alm.connectionId },
      }
    : null,
);
</script>

<template>
  <div class="pi-details-summary">
    <div class="card">
      <SvgIcon name="general/calendar" width="18" height="18" class="card-icon" />
      <span class="card-label">Date Created</span>
      <span class="card-value">{{ created }}</span>
    </div>

    <div class="card">
      <UserImage v-if="creator" :user="creator" size="small" class="card-icon" />
      <SvgIcon v-else name="persona/user" width="18" height="18" class="card-icon" />
      <span class="card-label">Creator</span>
      <span class="card-value">{{ creator?.name || "—" }}</span>
    </div>

    <!-- The whole card is the way into the event log -->
    <component
      :is="eventsLink ? 'router-link' : 'div'"
      class="card alm-card"
      :class="{ linked: !!eventsLink }"
      :to="eventsLink ?? undefined"
    >
      <template v-if="alm">
        <span class="card-icon alm-icon">
          <StatusDot v-if="status" :state="status.dot" />
          <SvgIcon :name="provider.icon" width="16" height="16" />
        </span>
        <span class="card-label">
          {{ status?.label ?? "ALM Status" }}
          · {{ provider.displayName }}
        </span>
        <span class="card-value alm-name">{{ alm.name || "ALM connection" }}</span>
        <span v-if="eventsLink" class="card-hint">
          View event log
          <SvgIcon name="general/external-link" width="12" height="12" />
        </span>
      </template>
      <template v-else>
        <SvgIcon name="general/alm-connection" width="18" height="18" class="card-icon muted" />
        <span class="card-label">ALM Status</span>
        <span class="card-value muted">{{ almPending ? "—" : "No connection" }}</span>
      </template>
    </component>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.pi-details-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.card {
  display: flex;
  flex-direction: column;
  align-items: center;

  // Shared min-height, so shorter cards would otherwise ride high
  justify-content: center;
  gap: 2px;

  // Room for the tallest card, or the strip jumps as the list loads
  min-height: 92px;
  padding: 10px 12px;
  border: 1px solid c.$border-subtle;
  border-radius: 8px;
  text-align: center;
  text-decoration: none;
  color: c.$text;
}

.alm-card.linked {
  cursor: pointer;
  transition:
    border-color 100ms ease,
    background-color 100ms ease;

  &:hover {
    border-color: c.$border;
    background-color: c.$background-grey-light;
  }

  &:focus-visible {
    outline: 2px solid c.$border-focus-color;
    outline-offset: 2px;
  }
}

.card-icon {
  color: c.$text-subtle;
  margin-bottom: 2px;
}

.alm-icon {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 18px;
}

.card-label {
  @include typography.extra-small("medium");

  color: c.$text-subtle;
}

.card-value {
  @include typography.small("semi-bold");

  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-hint {
  @include typography.extra-small;

  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: c.$brand-primary;
}

.muted {
  color: c.$text-subtle;
}
</style>
